import { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { CheckCircle, XCircle, ClockCounterClockwise, CalendarX, UserCheck, ArrowsClockwise } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { TrialIntegrationPanel } from "../components/trial/TrialIntegrationPanel";
import { CoachWeekBoard } from "../components/trial/CoachWeekBoard";

const STATUS_LABEL = { new: "Nouveau", confirmed: "Confirmé", expected: "Attendu", present: "Présent", absent: "Absent", reported: "Reporté", cancelled: "Annulé", to_enroll: "À inscrire", enrolled: "Inscrit", to_followup: "À relancer", lost: "Perdu" };
const STATUS_COLOR = { present: "bg-emerald-900/50 text-emerald-300", absent: "bg-red-900/50 text-red-300", reported: "bg-amber-900/50 text-amber-300", cancelled: "bg-zinc-800 text-zinc-400", enrolled: "bg-cyan-900/50 text-cyan-300", to_enroll: "bg-indigo-900/50 text-indigo-300" };

export default function CheckinEssaiModule() {
  const [tab, setTab] = useState("today");
  const [data, setData] = useState(null);
  const [recovery, setRecovery] = useState(null);
  const [evalFor, setEvalFor] = useState(null);
  const [integrationFor, setIntegrationFor] = useState(null);
  const [integrationTrial, setIntegrationTrial] = useState(null);
  const [ev, setEv] = useState({ recommended_program: "", recommended_schedule: "", impression: "interesse", next_action: "email_apres_essai", equipment: "" });

  const load = useCallback(() => {
    api.get("/leads/checkin/today").then((r) => setData(r.data)).catch(() => toast.error("Chargement échoué"));
    api.get("/leads/recovery").then((r) => setRecovery(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const checkin = async (id, status) => {
    try {
      const r = await api.post(`/leads/${id}/checkin`, { status });
      if (status === "present") toast.success(r.data.satisfaction_email_sent ? "Présent ✓ — courriel de satisfaction envoyé" : "Marqué présent ✓");
      else toast.success(`Statut : ${STATUS_LABEL[status]}`);
      load();
      if (status === "present") setEvalFor(id);
    } catch { toast.error("Échec"); }
  };
  const saveEval = async () => {
    try { await api.post(`/leads/${evalFor}/evaluation`, ev); toast.success("Évaluation enregistrée"); setEvalFor(null); setEv({ recommended_program: "", recommended_schedule: "", impression: "interesse", next_action: "email_apres_essai", equipment: "" }); load(); }
    catch { toast.error("Échec"); }
  };

  if (!data) return <div className="p-8 text-white/50">Chargement…</div>;

  return (
    <div className="p-6 sm:p-8 max-w-5xl mx-auto" data-testid="checkin-essai">
      <h1 className="text-3xl font-black text-white mb-1">🥋 Check-in — Cours d'essai</h1>
      <p className="text-white/50 text-sm mb-6">Accueil du jour · marque la présence, puis évalue en 5 champs.</p>

      <CoachWeekBoard onOpen={(tid) => setIntegrationTrial(tid)} />

      <div className="flex gap-2 mb-6">
        <button data-testid="tab-today" onClick={() => setTab("today")} className={`px-4 py-2 rounded-md text-sm font-semibold ${tab === "today" ? "bg-[#D90429] text-white" : "text-white/60 border border-white/15"}`}>Aujourd'hui ({data.date})</button>
        <button data-testid="tab-recovery" onClick={() => setTab("recovery")} className={`px-4 py-2 rounded-md text-sm font-semibold flex items-center gap-1.5 ${tab === "recovery" ? "bg-[#D90429] text-white" : "text-white/60 border border-white/15"}`}><ArrowsClockwise size={15} /> Récupération anciens essais</button>
        <button onClick={load} className="ml-auto text-white/40 hover:text-white text-sm">↻ Rafraîchir</button>
      </div>

      {tab === "today" && (
        <div className="space-y-3" data-testid="today-list">
          {data.leads.length === 0 && <p className="text-white/40 text-center py-10">Aucun cours d'essai prévu aujourd'hui.</p>}
          {data.leads.map((l) => (
            <div key={l.id} className="border border-white/10 bg-white/[0.02] rounded-lg p-4" data-testid={`lead-${l.id}`}>
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                  <p className="text-white font-bold">{l.name} <span className={`ml-2 text-[10px] uppercase px-2 py-0.5 rounded ${STATUS_COLOR[l.status] || "bg-white/10 text-white/60"}`}>{STATUS_LABEL[l.status] || l.status}</span></p>
                  <p className="text-white/50 text-xs mt-0.5">{l.program || "—"} · {l.phone || l.email} {l.instructor && `· coach ${l.instructor}`}</p>
                </div>
                <div className="flex gap-2 flex-wrap">
                  <button data-testid={`present-${l.id}`} onClick={() => checkin(l.id, "present")} className="px-3 py-1.5 rounded-md bg-emerald-600 text-white text-xs font-semibold flex items-center gap-1"><CheckCircle size={14} /> Présent</button>
                  <button data-testid={`absent-${l.id}`} onClick={() => checkin(l.id, "absent")} className="px-3 py-1.5 rounded-md border border-red-700 text-red-300 text-xs"><XCircle size={14} /> Absent</button>
                  <button onClick={() => checkin(l.id, "reported")} className="px-3 py-1.5 rounded-md border border-amber-700 text-amber-300 text-xs"><ClockCounterClockwise size={14} /> Reporté</button>
                  <button onClick={() => checkin(l.id, "cancelled")} className="px-3 py-1.5 rounded-md border border-white/15 text-white/50 text-xs"><CalendarX size={14} /> Annulé</button>
                  <button data-testid={`eval-${l.id}`} onClick={() => setEvalFor(l.id)} className="px-3 py-1.5 rounded-md border border-[#C9A961]/50 text-[#C9A961] text-xs flex items-center gap-1"><UserCheck size={14} /> Évaluer</button>
                  <button data-testid={`integration-${l.id}`} onClick={() => setIntegrationFor(l.id)} className="px-3 py-1.5 rounded-md bg-amber-500/90 hover:bg-amber-400 text-black text-xs font-bold flex items-center gap-1">🥋 Évaluation d'intégration</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "recovery" && recovery && (
        <div className="space-y-4" data-testid="recovery-list">
          <div className="flex flex-wrap gap-2">
            {Object.entries(recovery.counts).map(([st, n]) => (
              <span key={st} className={`text-xs px-2.5 py-1 rounded ${STATUS_COLOR[st] || "bg-white/10 text-white/60"}`} data-testid={`recovery-count-${st}`}>{STATUS_LABEL[st] || st} : {n}</span>
            ))}
          </div>
          {Object.entries(recovery.groups).map(([st, leads]) => (
            <div key={st} className="border border-white/10 rounded-lg p-3">
              <p className="text-white/70 text-sm font-bold mb-2">{STATUS_LABEL[st] || st} ({leads.length})</p>
              <div className="max-h-48 overflow-y-auto space-y-1">
                {leads.slice(0, 50).map((l) => (
                  <div key={l.id} className="text-xs text-white/60 flex justify-between">
                    <span>{l.name} · {l.program || "—"}</span>
                    <span className="text-white/30">{l.preferred_date || ""} · {l.source || ""}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {evalFor && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => setEvalFor(null)}>
          <div className="bg-zinc-950 border border-zinc-700 rounded-xl w-full max-w-lg p-5" onClick={(e) => e.stopPropagation()} data-testid="eval-modal">
            <h3 className="text-white font-bold mb-3">Mini-évaluation post-essai</h3>
            <div className="space-y-3">
              <input data-testid="eval-program" value={ev.recommended_program} onChange={(e) => setEv({ ...ev, recommended_program: e.target.value })} placeholder="Programme recommandé" className="w-full bg-black/30 border border-white/15 rounded px-3 py-2 text-sm text-white" />
              <input data-testid="eval-schedule" value={ev.recommended_schedule} onChange={(e) => setEv({ ...ev, recommended_schedule: e.target.value })} placeholder="Horaire recommandé" className="w-full bg-black/30 border border-white/15 rounded px-3 py-2 text-sm text-white" />
              <input value={ev.equipment} onChange={(e) => setEv({ ...ev, equipment: e.target.value })} placeholder="Équipements requis" className="w-full bg-black/30 border border-white/15 rounded px-3 py-2 text-sm text-white" />
              <select data-testid="eval-impression" value={ev.impression} onChange={(e) => setEv({ ...ev, impression: e.target.value })} className="w-full bg-black/30 border border-white/15 rounded px-3 py-2 text-sm text-white">
                <option value="enthousiaste">Enthousiaste</option><option value="interesse">Intéressé</option><option value="hesitant">Hésitant</option><option value="non_interesse">Non intéressé</option>
              </select>
              <select value={ev.next_action} onChange={(e) => setEv({ ...ev, next_action: e.target.value })} className="w-full bg-black/30 border border-white/15 rounded px-3 py-2 text-sm text-white">
                <option value="inscription_maintenant">Inscription maintenant</option><option value="email_apres_essai">Courriel après essai</option><option value="rappel_24h">Rappel 24 h</option><option value="rappel_3j">Rappel 3 jours</option>
              </select>
              <div className="flex gap-2">
                <button data-testid="eval-save" onClick={saveEval} className="flex-1 px-4 py-2 rounded-md bg-[#C9A961] text-black font-bold">Enregistrer</button>
                <button onClick={() => setEvalFor(null)} className="px-4 py-2 rounded-md border border-white/15 text-white/60">Fermer</button>
              </div>
            </div>
          </div>
        </div>
      )}
      {integrationTrial && (
        <Dialog open onOpenChange={(o) => !o && setIntegrationTrial(null)}>
          <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto bg-zinc-900 border-zinc-700 p-3 sm:p-5" aria-describedby={undefined} data-testid="checkin-week-dialog">
            <DialogHeader><DialogTitle className="text-sm text-zinc-400">Fiche d'intégration</DialogTitle></DialogHeader>
            <TrialIntegrationPanel trialId={integrationTrial} onChanged={load} />
          </DialogContent>
        </Dialog>
      )}
      {integrationFor && (
        <Dialog open onOpenChange={(o) => !o && setIntegrationFor(null)}>
          <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto bg-zinc-900 border-zinc-700 p-3 sm:p-5" aria-describedby={undefined} data-testid="checkin-integration-dialog">
            <DialogHeader><DialogTitle className="text-sm text-zinc-400">Évaluation d'intégration</DialogTitle></DialogHeader>
            <TrialIntegrationPanel leadId={integrationFor} onChanged={load} />
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
